import { supabase } from '../supabase'
import type { OutboxOp } from '../local/db'
import { classify, type Failure } from './errors'
import { failedOps, markFailed, markInflight, removeOp, scheduleRetry } from './outbox'
import { syncBus } from './bus'
import { healOrphanedOverlays, settleRow } from '../local/cache'

/**
 * Sends what the outbox holds, in order, and decides what to do when the
 * server says no.
 *
 * Two rules shape everything here.
 *
 * **Order is absolute within a row.** A ticket is inserted, then patched, then
 * settled. Send the patch first and it lands on a row that does not exist,
 * a permanent failure for a sale that was perfectly good. So a transient
 * failure stops the loop rather than skipping ahead.
 *
 * **A refusal must not stop the till.** Firestore rolled a rejected write back
 * and carried on; a queue cannot do that, because the entry stays at the head
 * and everything behind it waits for ever. So a permanently refused row is
 * quarantined, set aside, kept, made visible, and the queue moves past it.
 */

/**
 * Rows the server has refused. Their later writes wait; nothing else does.
 *
 * Rebuilt from the queue on first use rather than kept only in memory: a
 * refusal survives a reload, so this has to as well. Without that, refreshing
 * the page would send the blocked row's queued writes again, collect the same
 * refusal, and quarantine them one by one.
 */
const blocked = new Set<string>()
let blockedLoaded = false

async function loadBlocked(): Promise<void> {
  if (blockedLoaded) return
  blockedLoaded = true
  for (const op of await failedOps()) {
    blocked.add(`${op.table}:${op.rowId}`)
  }
}

let draining = false

/** Marks the last moment the server actually answered, see reachability. */
let lastOkAt = 0
export const lastServerContact = () => lastOkAt

function rowKey(op: OutboxOp): string {
  return `${op.table}:${op.rowId}`
}

/**
 * Pushes one op.
 *
 * Inserts go through `upsert` rather than `insert` deliberately. When a reply
 * is lost on the way back, common on a failing connection, the op is retried
 * and a plain insert would fail on the primary key: a permanent error, and a
 * quarantined sale, for a write that had already succeeded. Upsert makes the
 * retry a no-op instead.
 */
async function push(op: OutboxOp): Promise<Failure | null> {
  if (!supabase) {
    return { kind: 'transient', code: 'no-client', message: 'Supabase yapılandırılmamış' }
  }

  try {
    const table = supabase.from(op.table)

    const { error } =
      op.type === 'insert'
        ? await table.upsert({ ...op.payload, id: op.rowId })
        : await table
            .update(op.payload)
            .eq('id', op.rowId)
            .eq('tenant_id', op.tenantId)

    if (!error) return null
    return classify(error)
  } catch (err) {
    // fetch itself threw: no route to the host.
    return classify(err)
  }
}

/**
 * Works through the queue until it is empty or the network stops cooperating.
 *
 * Single-flight: `online` events, the interval and every write all call this,
 * and two loops running at once would send the same op twice.
 */
export async function drain(): Promise<void> {
  if (draining) return
  draining = true

  try {
    await loadBlocked()

    for (;;) {
      // Skips rows the server has already refused. Their queued writes stay
      // put, if the cashier retries the failed one, these follow behind it in
      // the order they were made.
      const op = await nextUnblocked()
      if (!op) return

      const stop = await send(op)
      if (stop) return
    }
  } finally {
    draining = false
  }
}

/** The oldest ready op whose row is not quarantined. */
async function nextUnblocked(): Promise<OutboxOp | undefined> {
  const { getDB } = await import('../local/db')
  const db = await getDB()
  const now = Date.now()
  const ready = await db.getAllFromIndex(
    'outbox',
    'by_status_time',
    IDBKeyRange.bound(['pending', 0], ['pending', now]),
  )
  return ready
    .sort((a, b) => a.nextAttemptAt - b.nextAttemptAt || a.seq - b.seq)
    .find((o) => !blocked.has(rowKey(o)))
}

/**
 * jsonb sorts object keys, so the server hands lines back in a different key
 * order than they were sent. Compared by content, not by text.
 */
function canon(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(canon).join(',')}]`
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>
    return `{${Object.keys(o)
      .filter((k) => o[k] !== undefined)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${canon(o[k])}`)
      .join(',')}}`
  }
  return JSON.stringify(v ?? null)
}

/**
 * Yeni adisyon kaydı reddedildi ama adisyon sunucuda zaten var (21 Eki 2026).
 *
 * Telefonda olan: kayıt sunucuya ulaştı, cevap zayıf bağlantıda geri dönmedi,
 * telefon tekrar gönderdi. Tekrar gönderilen upsert adisyonun ilk halini
 * taşıyor; bu arada kasa adisyonu taşıdıysa ya da kapattıysa orders_guard
 * reddediyor. Eskiden bu "takılan kayıt" oluyor ve telefonda adisyonun ilk
 * hali (eski masa, açık) sunucudakinin üstüne biniyordu; yenilemek düzeltmiyor,
 * yalnız uygulamayı silmek düzeltiyordu (garsonların açık adisyon sayısı).
 *
 * Kural: sunucudaki adisyon esas.
 * - Satırlar aynıysa telefonda gönderilecek bir şey yok: kayıt bırakılır.
 * - Adisyon hala açıksa ve telefonda fazladan tur varsa: yalnız satırlar
 *   gönderilir (masa adı ve durum sunucudaki gibi kalır).
 * - Adisyon kapanmış ve telefonda kaydedilmemiş tur varsa: takılan kayıt
 *   olarak kalır, karar insana (bu tur hiçbir yere gitmedi).
 */
async function resolveExistingTicket(op: OutboxOp): Promise<'resolved' | 'keep'> {
  if (op.type !== 'insert' || op.table !== 'orders' || !supabase) return 'keep'
  const { data, error } = await supabase
    .from('orders')
    .select('status, lines')
    .eq('id', op.rowId)
    .eq('tenant_id', op.tenantId)
    .maybeSingle()
  if (error || !data) return 'keep'

  const p = op.payload as Record<string, unknown>
  if (!('lines' in p) || canon(p.lines) === canon(data.lines)) return 'resolved'
  if (data.status !== 'open') return 'keep'

  const { error: upErr } = await supabase
    .from('orders')
    .update({
      lines: p.lines,
      item_count: p.item_count,
      total_kurus: p.total_kurus,
      updated_at_local: Date.now(),
    })
    .eq('id', op.rowId)
    .eq('tenant_id', op.tenantId)
  return upErr ? 'keep' : 'resolved'
}

/** Çözüldüyse kaydı kuyruktan ve telefondaki katmandan siler. */
async function settleExisting(op: OutboxOp): Promise<boolean> {
  const r = await resolveExistingTicket(op).catch(() => 'keep' as const)
  if (r !== 'resolved') return false
  await removeOp(op.seq)
  await settleRow(op.table, op.rowId, true)
  blocked.delete(rowKey(op))
  syncBus.emit('outbox-changed')
  console.warn('[sync] adisyon sunucuda vardı, sunucudaki esas alındı', op.rowId)
  return true
}

/** Açılışta: önceki sürümlerde bu yüzden takılmış kayıtları çözer. */
async function healExistingTickets(): Promise<void> {
  for (const op of await failedOps()) {
    if (op.type === 'insert' && op.table === 'orders') await settleExisting(op)
  }
}

/** Sends one op. Returns true when the loop should stop for now. */
async function send(op: OutboxOp): Promise<boolean> {
  await markInflight(op.seq)
  const failure = await push(op)

  if (!failure) {
    await removeOp(op.seq)
    // The overlay for this row can go once nothing else is queued for it.
    await settleRow(op.table, op.rowId, true)
    lastOkAt = Date.now()
    // A row that goes through clears any earlier block on it.
    blocked.delete(rowKey(op))
    syncBus.emit('outbox-changed')
    return false
  }

  if (failure.kind === 'permanent') {
    if (await settleExisting(op)) return false
    await markFailed(op, failure)
    blocked.add(rowKey(op))
    syncBus.emit('outbox-changed')
    // Deliberately loud and deliberately not only in the console: a refused
    // sale that reaches no further than devtools is the exact failure this
    // app has already been bitten by.
    syncBus.emit('permanent-failure', { rowId: op.rowId, failure })
    console.error('[sync] sunucu reddetti', op.table, op.rowId, failure)
    // Not a stop: everything else keeps flowing.
    return false
  }

  // Transient. Back off and stop, moving to the next op would reorder writes
  // against this same row.
  await scheduleRetry(op, failure)
  syncBus.emit('outbox-changed')
  return true
}

/** Nudges the drainer without waiting for it. */
export function kick(): void {
  void drain()
}

/**
 * Clears the quarantine for a row the cashier has chosen to retry.
 *
 * Only lifts the in-memory block. The queued entry itself is moved back to
 * pending by `retryFailed`, and the two are called together, this is what
 * the "Tekrar dene" button does.
 */
export function unblock(table: string, rowId: string): void {
  blocked.delete(`${table}:${rowId}`)
}

let started = false

/**
 * Starts the background loop.
 *
 * The interval matters as much as the events. `online` does not fire when a
 * captive portal starts letting traffic through, and it does not fire when the
 * server comes back, both of which happen in a hall full of people on one
 * access point.
 */
export function startDrainer(): () => void {
  if (started) return () => {}
  started = true

  // Önce eski cihazlarda biriken sahipsiz katmanlar; ardından ilk boşaltma.
  void healOrphanedOverlays()
    .then(() => healExistingTickets())
    .catch((err) => console.error('[sync] katman temizliği', err))
    .finally(() => kick())

  const onOnline = () => kick()
  const onVisible = () => {
    if (!document.hidden) kick()
  }

  window.addEventListener('online', onOnline)
  document.addEventListener('visibilitychange', onVisible)
  const timer = window.setInterval(kick, 15_000)

  kick()

  return () => {
    window.removeEventListener('online', onOnline)
    document.removeEventListener('visibilitychange', onVisible)
    window.clearInterval(timer)
    started = false
  }
}
